// [PURPOSE]: Pure helper that converts a single potion effect object into the
//   `appliedEffect` shape consumed by drinkPotion → setActiveQuickBonuses.
//   Extracted from App.jsx L4068-4125 to make the reader testable without
//   DOM-driving the full drinkPotion closure (Rule 22 — tests exercise production
//   code, not inline replicas).
//
// [RULE 31(c) FIX]: App.jsx's resistance branch (L4083-4095 pre-extraction) read
//   `effect.target` for both `affects` and `damageType`. After the 1I(a) rename
//   (commit d115bd0) all gameData.js resistance rows use `value:` not `target:`,
//   matching the canonical schema at effectRegistry.js L44. This helper reads
//   `effect.value` for the resistance type, eliminating the post-1I(a) regression
//   where `damageType` was `undefined` and `affects` showed "damage damage".
//
// [SCHEMA NOTE]: `ability` effects use `target:` (e.g. 'str', 'dex') — that is
//   intentional per effectRegistry.js L1340-1341. `resistance` effects use `value:`
//   (effectRegistry.js L44). `speed` effects use `target:` for the speed type.
//   `condition` effects use `target:` for the conditionKey (e.g. 'poisoned', 'invisible').
//
// [CONSUMERS]: App.jsx drinkPotion handler.
// [TESTS]: src/__tests__/sub-plan-1I-c-h-app-jsx-potion-apply.test.js
//          src/__tests__/sub-plan-1U-impl-H-condition-branch-runtime.test.js

import { SRD_CONDITIONS } from '../data/srdConditions';

/**
 * Build the appliedEffect object for a single potion effect.
 *
 * @param {Object} item   - The inventory item being consumed (needs `.name`)
 * @param {Object} effect - One effect from `item.effects[]`
 * @returns {Object|null}  The appliedEffect shape, or null if the effect type
 *                         is handled elsewhere (healing / damage → immediate) or
 *                         is unknown.
 */
export function buildAppliedPotionEffect(item, effect) {
  if (!item || !effect) return null;

  // Ability score SET effect (Giant Strength potions)
  // `target:` is the canonical key for ability effects (effectRegistry.js L1340-1341)
  if (effect.type === 'ability' && effect.operation === 'set') {
    return {
      name: `Potion: ${item.name}`,
      bonus: effect.value,
      affects: `${effect.target?.toUpperCase() || 'Ability'} (SET)`,
      duration: effect.duration || '1 hour',
      icon: '💪',
      type: 'setAbility',
      ability: effect.target,
      value: effect.value,
      // [Sub-plan 1U-impl-A 2026-05-27]: effects[] required for effectRegistry registration.
      // drinkPotion calls effectRegistry.register(`buff:${name}`, effects) at the
      // setActiveQuickBonuses append site (mirroring QuickBonusesPanel.js:536).
      // Without this array, syncQuickBonusesToRegistry regex matched 'setAbility' but
      // produced target='ability' (not in abilities list) → effects=[] → nothing registered
      // → getEffectiveAbilityScore returned base STR unchanged. STR=15 not 25.
      effects: [{
        type: 'ability',
        target: effect.target,
        operation: 'set',
        value: effect.value,
        condition: 'always'
      }]
    };
  }

  // Resistance effect
  // SCHEMA (effectRegistry.js L44): resistance uses `value: <damageType>`, NOT target.
  // The 1I(a) rename (d115bd0) fixed gameData.js to emit `value:`.
  // This was the parallel-reader regression — pre-extraction App.jsx read `effect.target`
  // and got `undefined` after the rename. The fix is `effect.value` here.
  if (effect.type === 'resistance') {
    const dmgType = effect.value || 'damage';
    return {
      name: `Potion: ${item.name}`,
      bonus: 'Resistance',
      affects: `${dmgType} damage`,
      duration: effect.duration || '1 hour',
      icon: '🛡️',
      type: 'resistance',
      damageType: effect.value
    };
  }

  // Speed effect (climbing, swimming, flying)
  // `target:` is the canonical key for speed-type routing (SensesMovementPanel.js L340-350)
  if (effect.type === 'speed') {
    const speedIcons = { climbSpeed: '🧗', swimSpeed: '🏊', flySpeed: '🦅' };
    return {
      name: `Potion: ${item.name}`,
      bonus: effect.operation === 'match' ? 'Equal to walk speed' : `${effect.value} ft`,
      affects: effect.target || 'Movement',
      duration: effect.duration || '1 hour',
      icon: speedIcons[effect.target] || '🏃',
      type: 'speed',
      speedType: effect.target,
      value: effect.value
    };
  }

  // Temporary HP effect (Potion of Heroism, future Aid-spell handler)
  // [Sub-plan 1Q-F-5a 2026-05-28]: D&D 5e temp HP rule (PHB p.198) — temp HP
  //   doesn't stack with itself. New value REPLACES current if higher; otherwise no change.
  //   This appliedEffect shape carries value through to drinkPotion which calls gainTempHP
  //   (App.jsx L5930) for the max() rule + activity log + status toast.
  //   Integration: App.jsx drinkPotion handler checks appliedEffect.type === 'tempHP'
  //   and applies immediately via gainTempHP, then appends to activeQuickBonuses for the
  //   buff-timer UI.
  if (effect.type === 'tempHP') {
    return {
      name: `Potion: ${item.name}`,
      bonus: `${effect.value} temp HP`,
      affects: 'Temporary HP',
      duration: effect.duration || '1 hour',
      icon: '💙',
      type: 'tempHP',
      value: effect.value
    };
  }

  // Spell effect (potions that cast a spell or grant being-under-its-effect)
  // [Sub-plan 1Q-F-5b 2026-05-28]: 8 potion sites in gameData.js (Bless on Heroism,
  //   Haste on Potion of Speed, Animal Friendship, Clairvoyance, Detect Thoughts,
  //   Enlarge/Reduce ×2, Gaseous Form). Two operations:
  //     'cast'  — potion casts the spell one-shot (surface in UI + log; full mechanical
  //               resolution deferred — see breadcrumb 1Q-F-5b-i)
  //     'grant' — under-the-effect-of for duration. Stamps type to spellName.toLowerCase()
  //               so existing mechanical handlers fire:
  //               - type:'bless' → b.type === 'bless' in App.jsx L3497, EquipmentPanel.js L267,
  //                 effectCalculator.js L1665 (+1d4 attack/save rolls)
  //               - type:'haste' → b.type === 'haste' in QuickBonusesPanel.js L305 (backward compat)
  //   [BREADCRUMB Rule 25 — Sub-plan 1Q-F-5b-i]: 'cast' operation full mechanical resolution
  //   (Animal Friendship DC 13 save, Clairvoyance scrying, Detect Thoughts DC 13) is deferred.
  //   This patch surfaces the cast event for UI awareness; the spell's own mechanics need a
  //   separate integration arc.
  //   [NOTE — Rule 11/17 anti-pattern in consumers]: b.type === 'bless' + b.type === 'haste' are
  //   pre-existing hardcoded detections in 3 files. NOT this patch's scope to retire. See
  //   investigation note in 2026-05-28-patch-5-1Q-F-5b-spell-on-potion-wire-d8e9f0.md re: whether
  //   Sub-plan 1Q-F-5b-ii is needed (it is — not yet in master-queue at patch time).
  if (effect.type === 'spell') {
    const spellName = effect.target || 'Unknown Spell';
    const isGrant = effect.operation === 'grant';
    // For 'grant': type must be spellName.toLowerCase() so existing b.type === 'bless'/'haste'
    // mechanical handlers fire (App.jsx L3497, EquipmentPanel.js L267, effectCalculator.js L1665).
    // For 'cast': type is 'spell' — no existing mechanical handler; just UI surfacing.
    const entryType = isGrant ? spellName.toLowerCase() : 'spell';
    // [Sub-plan 1U-impl-B 2026-05-27]: For 'grant' operations on dice-affecting spells (Bless),
    // the QB entry's `affects` string must include the literal substring 'saves' so
    // QuickActionsBar.js:224 `bonus.affects?.includes('saves')` fires on saving throw rolls.
    // Without this fix, Heroism-granted Bless applied +1d4 to attacks (via b.type==='bless'
    // hardcoded checks) but NOT to saves (the saves reader uses the affects string, not the type).
    // IMPORTANT: QuickActionsBar.js:224 checks .includes('saves') — the preset Bless uses
    // 'Attack rolls, saving throws' which does NOT contain literal 'saves' (it has 'saving').
    // Using 'Attack rolls, saves' ensures .includes('saves') === true for potion-granted Bless.
    // The preset Bless saves path via QuickActionsBar:224 is separately broken; fixing it is
    // out of scope for this dispatch (would require editing QuickActionsBar.js — Rule 32 deferred).
    const spellGrantAffectsMap = {
      bless: 'Attack rolls, saves',
      // Extend here as future potion-granted dice-affecting spells are added.
    };
    const grantAffects = (isGrant && spellGrantAffectsMap[entryType]) || 'Active Spell';
    return {
      name: isGrant ? spellName : `Potion: ${item.name}`,
      bonus: isGrant ? `Granted: ${spellName}` : `Cast ${spellName}`,
      affects: isGrant ? grantAffects : 'Spell Cast',
      duration: effect.duration || '1 hour',
      icon: '✨',
      type: entryType,
      spellName,
      operation: effect.operation,
      dc: effect.dc,
      saveAbility: effect.saveAbility || null,
      noConcentration: effect.noConcentration || false,
      sourceItem: item.name
    };
  }

  // Special effect (water breathing, growth, etc.)
  if (effect.type === 'special') {
    return {
      name: `Potion: ${item.name}`,
      bonus: effect.description || effect.target,
      affects: 'Special',
      duration: effect.duration || '1 hour',
      icon: effect.icon || '✨',
      type: 'special',
      specialType: effect.target
    };
  }

  // Condition effect (poisoned, invisible, paralyzed, etc.)
  //
  // [Sub-plan 1U-impl-H 2026-05-28]: QB-entry routing (not conditionsPool write).
  //   Rationale: `character.conditions` is a plain string array with no duration,
  //   saveDC, or recurringDamage metadata. All time-bounded potion effects live in
  //   activeQuickBonuses so the buff-timer UI, chip row, and duration display work
  //   correctly. The conditionsPool write (to surface the chip in ConditionsTracker)
  //   is scoped to Sub-plan 1Q-F-7d which bridges QB → ConditionsTracker.
  //   Endgame note: PF2e/Mouseguard/CoC condition-like states also carry duration +
  //   severity metadata — QB-entry shape is data-block-engine-friendly; string-array
  //   conditionsPool is 5e-specific. This branch encodes WHAT, not HOW.
  //
  //   [BREADCRUMB Rule 25 — Sub-plan 1Q-F-7d]: `conditionKey` in the QB entry is the
  //   bridge field for ConditionsTracker wiring. When 1Q-F-7d lands, it reads
  //   `entry.conditionKey` from activeQuickBonuses and surfaces it to ConditionsTracker.
  //
  //   [BREADCRUMB Rule 25 — v1.x Combat Tracker arc]: `recurringDamage` + `saveDC` +
  //   `saveAbility` fields are recorded here but NOT yet ticked per-turn. The Combat
  //   Tracker arc (deferred to v1.x) will read these fields to apply per-turn recurring
  //   damage and prompt the save-repeat check. For now, the initial damage is handled
  //   by the `damage` effect type (effect[0] on potion_poison) and the save prompt on
  //   drink is deferred — the condition is applied unconditionally on drink (permissive,
  //   user-visible, consistent with other time-bounded potion effects).
  //
  //   Affected potions:
  //   - potion_poison:       target:'poisoned', saveDC:13, saveAbility:'con', recurringDamage:'3d6'
  //   - potion_invisibility: target:'invisible', duration:'1 hour'
  if (effect.type === 'condition') {
    const conditionKey = effect.target || 'unknown';
    const conditionData = SRD_CONDITIONS[conditionKey] || {};
    const conditionName = conditionData.name || conditionKey;
    const icon = conditionData.icon || '🎯';
    return {
      name: `Potion: ${item.name}`,
      bonus: `Condition: ${conditionName}`,
      affects: conditionName,
      duration: effect.duration || 'Until save',
      icon,
      type: 'condition',
      conditionKey,
      saveDC: effect.saveDC || null,
      saveAbility: effect.saveAbility || null,
      recurringDamage: effect.recurringDamage || null,
      damageType: effect.damageType || null,
      operation: effect.operation || 'grant'
    };
  }

  // -------------------------------------------------------------------------
  // [SUB-PLAN 1H-G Stage 2 — 2026-05-29]: Runtime extensions for the 3 types
  //   that previously returned null. Each branch returns a standard QB-entry
  //   appliedEffect consumed by drinkPotionLoop.js catch-all → setActiveQuickBonuses.
  //   No drinkPotionLoop.js or App.jsx change required — catch-all handles non-null.
  // -------------------------------------------------------------------------

  // Feature effect (climbSpeed, flySpeed, waterBreathing)
  // SRD shapes:
  //   { type:'feature', target:'climbSpeed', operation:'set', value:'walk', duration:'1 hour' }
  //   { type:'feature', target:'flySpeed', operation:'equal_to_walk_speed', duration:'1 hour' }
  //   { type:'feature', target:'waterBreathing', operation:'grant', duration:'1 hour' }
  //
  // climbSpeed / flySpeed — mirror the `speed` branch (speedType + value).
  //   operation:'set' with value:'walk' or 'equal_to_walk_speed' means
  //   speed = walk speed (computed by SensesMovementPanel at display time).
  // waterBreathing — feature flag QB entry (mirror `special`).
  if (effect.type === 'feature') {
    const target = effect.target || 'unknown';
    if (target === 'climbSpeed' || target === 'flySpeed') {
      const speedIcons = { climbSpeed: '🧗', flySpeed: '🦅' };
      const valueDisplay = (effect.operation === 'set' && effect.value === 'walk') ||
        effect.operation === 'equal_to_walk_speed'
        ? 'Equal to walk speed'
        : `${effect.value} ft`;
      return {
        name: `Potion: ${item.name}`,
        bonus: valueDisplay,
        affects: target,
        duration: effect.duration || '1 hour',
        icon: speedIcons[target] || '🏃',
        type: 'feature',
        featureTarget: target,
        speedType: target,
        operation: effect.operation,
        value: effect.value
      };
    }
    // waterBreathing and other feature-flag targets — mirror `special`
    return {
      name: `Potion: ${item.name}`,
      bonus: effect.description || target,
      affects: 'Special',
      duration: effect.duration || '1 hour',
      icon: '💧',
      type: 'feature',
      featureTarget: target,
      operation: effect.operation
    };
  }

  // Damage reduction effect — grants resistance to ALL damage types for duration.
  // SRD shape: { type:'damageReduction', operation:'all', duration:'1 minute' }
  // Mirrors the `resistance` branch but affects 'all' damage rather than a single type.
  if (effect.type === 'damageReduction') {
    return {
      name: `Potion: ${item.name}`,
      bonus: 'Resistance to All',
      affects: 'all damage',
      duration: effect.duration || '1 minute',
      icon: '🛡️',
      type: 'damageReduction',
      operation: effect.operation || 'all'
    };
  }

  // Breath weapon effect — grants a usable fire-breath ability for duration.
  // SRD shape: { type:'breathWeapon', operation:'roll', value:'4d6',
  //              damageType:'fire', saveDC:13, saveAbility:'dex',
  //              duration:'1 hour', uses:3 }
  //
  // This QB entry surfaces the usable breath-weapon ability (name, uses remaining,
  // breath params) so the player sees they have N fire-breaths available.
  //
  // [BREADCRUMB Rule 25 — Sub-plan 1Q-F-5b parallel: breathWeapon per-turn resolution]
  //   Full per-turn bonus-action fire-breath resolution (roll + target save + damage application)
  //   is deferred. Per-turn fire-breath requires a "usable ability action" system (player clicks
  //   the ability, picks targets, rolls 4d6 fire, targets make DC 13 DEX save) that is outside
  //   the scope of the transient QB-entry pattern. The QB entry here surfaces the available
  //   uses so the player is aware; the combat action itself is a separate integration arc.
  //   Cross-reference: master-queue.md for the deferred breathWeapon per-turn arc.
  if (effect.type === 'breathWeapon') {
    const damageType = effect.damageType || 'fire';
    const usesRemaining = effect.uses || 3;
    const diceNotation = effect.value || '4d6';
    return {
      name: `Potion: ${item.name}`,
      bonus: `${usesRemaining} uses — ${diceNotation} ${damageType}`,
      affects: `Breath Weapon (${damageType})`,
      duration: effect.duration || '1 hour',
      icon: '🔥',
      type: 'breathWeapon',
      damageType,
      saveDC: effect.saveDC || null,
      saveAbility: effect.saveAbility || null,
      diceNotation,
      usesRemaining,
      usesMax: usesRemaining
    };
  }

  // healing and damage effects are handled directly in drinkPotion (immediate application)
  // spell effects are handled above (type:'spell' case)
  return null;
}
